import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAdmins, deleteAdmin } from "../../utils/backend";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCard,
  CCardBody,
  CFormInput,
  CButton,
  CFormSelect,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CRow,
  CCol,
  CInputGroup,
} from "@coreui/react-pro";

import { CPagination, CPaginationItem } from "@coreui/react-pro";
import { toast } from "react-toastify";
import CIcon from "@coreui/icons-react";
import {
  cilSwapVertical,
  cilSortAlphaUp,
  cilSortAlphaDown,
  cilSortNumericUp,
  cilSortNumericDown,
} from "@coreui/icons";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";
import { setUppercase } from "../../utils/SetUppercase";

const generatePerPageOptions = (totalItems) => {
  if (totalItems <= 10) {
    return [10];
  }
  const options = [];
  let interval = 10;
  while (interval < totalItems) {
    options.push(interval);
    if (interval >= 50) {
      interval += 50;
    } else {
      interval *= 5;
    }
  }
  const nearestHigherMultipleOf50 = Math.ceil(totalItems / 50) * 50;
  options.push(nearestHigherMultipleOf50);
  return options;
};

const Admins = () => {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const localStorageAdminId = localStorage.getItem("id");

  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc"; // Toggle back to ascending
    }
    setSortConfig({ key, direction });
    setAdmins((prevAdmins) => [...prevAdmins]); // Trigger re-render to apply styles
  };

  const sortedAdmins = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return admins;
    return [...admins].sort((a, b) => {
      const aValue =
        typeof a[sortConfig.key] === "string"
          ? a[sortConfig.key].toLowerCase()
          : a[sortConfig.key];
      const bValue =
        typeof b[sortConfig.key] === "string"
          ? b[sortConfig.key].toLowerCase()
          : b[sortConfig.key];
      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [admins, sortConfig]);

  const hasPermission = (permissionGroup, permissionName) => {
    let permissions;
    try {
      permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      permissions = [];
    }

    return permissions.some(
      (perm) =>
        perm.permission_group === permissionGroup &&
        perm.permission_name === permissionName
    );
  };

  useEffect(() => {
    // Load filters and pagination state from localStorage on page load
    const savedState = JSON.parse(localStorage.getItem("adminsState"));
    if (savedState) {
      setUsername(savedState.username || "");
      setFullName(savedState.fullName || "");
      setEmail(savedState.email || "");
      setStartDate(savedState.startDate || "");
      setEndDate(savedState.endDate || "");
      setPerPage(savedState.perPage || 10);

      // Fetch data based on the saved state
      // Always land on page 1. Restoring a saved page number stranded admins on
      // stale rows: the list is sorted newest-first, so page N>1 never shows new
      // rows and nothing on screen explained why.
      const filters = {
        username: savedState.username || "",
        full_name: savedState.fullName || "",
        email: savedState.email || "",
        start_date: savedState.startDate || "",
        end_date: savedState.endDate || "",
        page: 1,
        perPage: savedState.perPage || 10,
      };
      fetchData(filters);
    } else {
      fetchData();
    }
  }, []);

  const saveStateToLocalStorage = () => {
    const existingState = JSON.parse(localStorage.getItem("adminsState")) || {};
    // Evict keys written by older builds; the merge below would otherwise keep a
    // stale currentPage alive in every admin's browser indefinitely.
    delete existingState.currentPage;
    const state = {
      username,
      fullName,
      email,
      startDate,
      endDate,
      perPage,
    };
    localStorage.setItem(
      "adminsState",
      JSON.stringify({ ...existingState, ...state })
    );
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeout);
  }, [username, fullName, email, startDate, endDate, perPage]);

  const handleSearch = async () => {
    const filters = {
      username,
      full_name: fullName,
      email,
      start_date: startDate,
      end_date: endDate,
      page: 1,
      perPage: perPage,
    };
    saveStateToLocalStorage(); // Save state before fetching data
    await fetchData(filters);
  };

  const handleReset = () => {
    setUsername("");
    setFullName("");
    setEmail("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);

    // Retain only perPage in localStorage
    const existingState = JSON.parse(localStorage.getItem("adminsState")) || {};
    localStorage.setItem(
      "adminsState",
      JSON.stringify({ perPage: existingState.perPage || 10 })
    );

    fetchData();
  };

  const handlePageChange = async (page) => {
    const filters = {
      username,
      full_name: fullName,
      email,
      start_date: startDate,
      end_date: endDate,
      page: page,
      perPage: perPage,
    };
    await fetchData(filters);
  };

  const handlePerPageChange = async (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to the first page
    localStorage.setItem(
      "adminsState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("adminsState")),
        perPage: newPerPage,
      })
    );
    const filters = {
      username,
      full_name: fullName,
      email,
      start_date: startDate,
      end_date: endDate,
      page: 1,
      perPage: newPerPage,
    };
    await fetchData(filters);
  };

  const fetchData = async (filters = { perPage: perPage }) => {
    //setIsLoading(true);
    try {
      const response = await getAdmins(filters);
      setAdmins(setUppercase(response.data.data));
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAdmins([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAdmin({ id: adminToDelete });
      toast.success("Admin deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.message);
      console.error("Failed to delete admin:", error);
    } finally {
      setDeleteModalVisible(false);
    }
  };

  // Surfaced next to Reset so an admin can never be silently filtered down to a subset.
  const activeFilterCount = [
    username,
    fullName,
    email,
    startDate,
    endDate,
  ].filter(function (v) {
    return v !== "" && v !== null && v !== undefined;
  }).length;

  return (
    <>
      <CCard className="border-light">
        <CCardBody style={{ overflowX: "auto" }}>
          {isLoading ? (
            <div>
              <Skeleton height={40} count={1} />
              <Skeleton height={30} count={5} style={{ marginTop: "10px" }} />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <CRow>
                <CCol className="justify-content-end d-flex my-1 gap-3 align-items-center">
                  {activeFilterCount > 0 && (
                    <span
                      className="me-3 text-nowrap"
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffe69c",
                        borderRadius: "12px",
                        color: "#664d03",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        padding: "4px 10px",
                      }}
                    >
                      <i className="bi bi-funnel-fill me-1"></i>
                      {activeFilterCount}{" "}
                      {activeFilterCount === 1 ? "filter" : "filters"} active —
                      showing a subset
                    </span>
                  )}
                  {hasPermission("Admin Management", "Create") && (
                    <CButton
                      className="ansa-button-primary"
                      onClick={() => navigate("/admins/create")}
                    >
                      Create New Admin
                    </CButton>
                  )}
                  <CButton
                    className="ansa-button-secondary me-3"
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </CButton>
                </CCol>
              </CRow>
              <CTable borderless striped>
                <CTableHead>
                  <CTableRow>
                    <CTableDataCell></CTableDataCell>
                    <CTableDataCell></CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </CTableDataCell>
                    <CInputGroup style={{ maxWidth: "250px" }}>
                      <CFormInput
                        type="date"
                        placeholder="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        title="Start Date"
                      />

                      <span className="input-group-text bg-light text-muted small">
                        to
                      </span>

                      <CFormInput
                        type="date"
                        placeholder="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        title="End Date"
                      />
                    </CInputGroup>
                  </CTableRow>
                  <CTableRow>
                    <CTableHeaderCell
                      onClick={() => {}}
                      className={`hover-header ${
                        sortConfig.key === "id" ? "ctable-active-header" : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        width: "80px", // Slightly wider column
                        fontSize: "1rem", // Match text size to lg icons
                      }}
                    >
                      No.{" "}
                      {sortConfig.key === "id" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortNumericDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortNumericUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.5 }}
                        />
                      )}
                    </CTableHeaderCell>
                    <CTableHeaderCell>Actions</CTableHeaderCell>
                    <CTableHeaderCell
                      onClick={() => handleSort("username")}
                      className={`hover-header ${
                        sortConfig.key === "username"
                          ? "ctable-active-header"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem", // Match text size to lg icons
                      }}
                    >
                      Username{" "}
                      {sortConfig.key === "username" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortAlphaDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortAlphaUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.5 }}
                        />
                      )}
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      onClick={() => handleSort("full_name")}
                      className={`hover-header ${
                        sortConfig.key === "full_name"
                          ? "ctable-active-header"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem", // Match text size to lg icons
                      }}
                    >
                      Full Name{" "}
                      {sortConfig.key === "full_name" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortAlphaDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortAlphaUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.5 }}
                        />
                      )}
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      onClick={() => handleSort("email")}
                      className={`hover-header ${
                        sortConfig.key === "email" ? "ctable-active-header" : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem", // Match text size to lg icons
                      }}
                    >
                      Email{" "}
                      {sortConfig.key === "email" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortAlphaDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortAlphaUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.5 }}
                        />
                      )}
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      onClick={() => handleSort("created_at")}
                      className={`hover-header ${
                        sortConfig.key === "created_at"
                          ? "ctable-active-header"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem", // Match text size to lg icons
                        width: "250px",
                      }}
                    >
                      Created At{" "}
                      {sortConfig.key === "created_at" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortNumericDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortNumericUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.5 }}
                        />
                      )}
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {sortedAdmins.map((admin, index) => (
                    <CTableRow key={admin.id}>
                      <CTableDataCell>
                        {index + 1 + perPage * (currentPage - 1)}
                      </CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex align-items-center gap-2">
                          {hasPermission("Admin Management", "View") && (
                            <CButton
                              className="btn btn-sm btn-info text-white"
                              title="Details"
                              onClick={() =>
                                navigate(`/admins/details/${admin.id}`)
                              }
                              disabled={admin.username === "superadmin"}
                            >
                              <i className="bi bi-eye-fill"></i>
                            </CButton>
                          )}
                          {hasPermission("Admin Management", "Edit") && (
                            <CButton
                              className="btn btn-sm btn-warning text-white"
                              title="Edit"
                              onClick={() =>
                                navigate(`/admins/edit/${admin.id}`)
                              }
                              disabled={admin.username === "superadmin"}
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </CButton>
                          )}
                          {hasPermission("Admin Management", "Delete") && (
                            <CButton
                              className="btn btn-sm btn-danger text-white"
                              title="Delete"
                              onClick={() => {
                                setAdminToDelete(admin.id);
                                setDeleteModalVisible(true);
                              }}
                              disabled={
                                admin.username === "superadmin" ||
                                String(admin.id) === localStorageAdminId
                              }
                            >
                              <i className="bi bi-trash-fill"></i>
                            </CButton>
                          )}
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>{admin.username || "N/A"}</CTableDataCell>
                      <CTableDataCell>
                        {admin.full_name || "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>{admin.email || "N/A"}</CTableDataCell>
                      <CTableDataCell>
                        {admin.created_at
                          ? formatDateTime(admin.created_at)
                          : "N/A"}
                      </CTableDataCell>{" "}
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          )}

          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <CPagination
                  className="mx-2"
                  aria-label="Page navigation example"
                  onActivePageChange={async (page) => {
                    setCurrentPage(page);
                    await fetchData(
                      {
                        username,
                        full_name: fullName,
                        email,
                        start_date: startDate,
                        end_date: endDate,
                        perPage: perPage,
                      },
                      page
                    );
                  }}
                  activePage={currentPage}
                  pages={totalPages}
                  doubleArrows={false}
                  arrows
                >
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </CPaginationItem>
                  {[...Array(totalPages).keys()].map((pageNumber) => {
                    if (
                      pageNumber === 0 || // First page
                      pageNumber === totalPages - 1 || // Last page
                      pageNumber < 5 || // First few pages
                      (pageNumber >= currentPage - 2 &&
                        pageNumber <= currentPage + 2) // Pages around the current page
                    ) {
                      return (
                        <CPaginationItem
                          key={pageNumber + 1}
                          className={
                            currentPage === pageNumber + 1 ? "active-page" : ""
                          }
                          onClick={() => handlePageChange(pageNumber + 1)}
                        >
                          {pageNumber + 1}
                        </CPaginationItem>
                      );
                    } else if (
                      (pageNumber === 5 && currentPage > 7) || // Ellipsis after the first few pages
                      (pageNumber === currentPage + 3 &&
                        currentPage < totalPages - 4) // Ellipsis before the last page
                    ) {
                      return (
                        <CPaginationItem
                          key={`ellipsis-${pageNumber}`}
                          disabled
                        >
                          ...
                        </CPaginationItem>
                      );
                    } else {
                      return null;
                    }
                  })}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </CPaginationItem>
                </CPagination>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3">
                  Showing {admins.length} of {totalItems} admins
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                >
                  {generatePerPageOptions(totalItems).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
      <CModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        alignment="center"
      >
        <CModalHeader>
          <CModalTitle>Confirm Delete</CModalTitle>
        </CModalHeader>
        <CModalBody>Are you sure you want to delete this admin?</CModalBody>
        <CModalFooter>
          <CButton
            className="ansa-button-secondary"
            onClick={() => setDeleteModalVisible(false)}
          >
            Cancel
          </CButton>
          <CButton className="ansa-button-primary" onClick={handleDelete}>
            Delete
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default Admins;
